import ExportLink from 'components/common/ExportLink';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { COUPON_CLASSIFICATIONS, MENU_DISPLAY_TIMING, MENU_PROPERTIES, MENU_TYPES } from '../constants';
import { MenuDetail } from '../models/menu';

interface ExportCSVProps {
  data: MenuDetail[];
}

export interface ExportCsvRef {
  onClick: () => void;
}

const ExportCSV = forwardRef<ExportCsvRef, ExportCSVProps>((props, ref) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLAnchorElement | null>(null);
  const menu_properties = [...MENU_PROPERTIES];
  menu_properties.shift();
  type ResultType = {
    [key: string]: any;
  };

  const extraData = props.data.map((object) => {
    const menu_type = MENU_TYPES.find((x) => x.value === object.menu.type);
    const menu_display_timing = MENU_DISPLAY_TIMING.find((x) => x.value === object.menu.display_timing);
    let menuPropertyObj: ResultType = {};
    menu_properties.forEach((prop, idx) => {
      const property = object.menu.menu_properties.find((p) => p.property === prop.value);
      const text = property && property.is_active ? '〇' : 'ー';
      menuPropertyObj['menu_property' + prop.value] = text;
    });
    const menuObject = {
      menu_code: object.menu.food_system_menu.cd + '' + COUPON_CLASSIFICATIONS[object.menu.coupon_classification],
      menu_name: object.name,
      menu_type: menu_type?.display_name || '',
      display_timing: menu_display_timing?.label || '',
      img_file_name: object.menu.img || '',
      display_name: object.menu.display_name,
      display_name_ja: object.menu.display_name_ja,
      display_name_en: object.menu.display_name_en,
      display_name_vi: object.menu.display_name_vi,
      display_name_ko: object.menu.display_name_ko,
      display_name_zh_hant: object.menu.display_name_zh_hant,
      display_name_zh_hans: object.menu.display_name_zh_hans,
      explanation: object.menu.explanation,
      explanation_ja: object.menu.explanation_ja,
      explanation_en: object.menu.explanation_ja,
      explanation_vi: object.menu.explanation_ja,
      explanation_ko: object.menu.explanation_ja,
      explanation_zh_hant: object.menu.explanation_ja,
      explanation_zh_hans: object.menu.explanation_ja,
      annotation_1: object.menu.note1,
      annotation1_ja: object.menu.note1_ja,
      annotation1_en: object.menu.note1_en,
      annotation1_vi: object.menu.note1_vi,
      annotation1_ko: object.menu.note1_ko,
      annotation1_zh_hant: object.menu.note1_zh_hant,
      annotation1_zh_hans: object.menu.note1_zh_hans,
      annotation_2: object.menu.note2,
      annotation2_ja: object.menu.note2_ja,
      annotation2_en: object.menu.note2_en,
      annotation2_vi: object.menu.note2_vi,
      annotation2_ko: object.menu.note2_ko,
      annotation2_zh_hant: object.menu.note2_zh_hant,
      annotation2_zh_hans: object.menu.note2_zh_hans,
      remarks: object.menu.note,
    };
    return { ...menuObject, ...menuPropertyObj };
  });

  const properties_headers = menu_properties.map((property) => {
    return { header: property.label, key: 'menu_property' + property.value };
  });
  const headers = [
    { header: t('MenuList.HeaderExportCsv.menu_code'), key: 'menu_code' },
    { header: t('MenuList.HeaderExportCsv.menu_name'), key: 'menu_name' },
    { header: t('MenuList.HeaderExportCsv.menu_type'), key: 'menu_type' },
    { header: t('MenuList.HeaderExportCsv.display_timing'), key: 'display_timing' },
    { header: t('MenuList.HeaderExportCsv.img_file_name'), key: 'img_file_name' },
    { header: t('MenuList.HeaderExportCsv.display_name'), key: 'display_name' },
    { header: t('MenuList.HeaderExportCsv.display_name_ja'), key: 'display_name_ja' },
    { header: t('MenuList.HeaderExportCsv.display_name_en'), key: 'display_name_en' },
    { header: t('MenuList.HeaderExportCsv.display_name_vi'), key: 'display_name_vi' },
    { header: t('MenuList.HeaderExportCsv.display_name_ko'), key: 'display_name_ko' },
    { header: t('MenuList.HeaderExportCsv.display_name_zh_hant'), key: 'display_name_zh_hant' },
    { header: t('MenuList.HeaderExportCsv.display_name_zh_hans'), key: 'display_name_zh_hans' },
    { header: t('MenuList.HeaderExportCsv.explanation'), key: 'explanation' },
    { header: t('MenuList.HeaderExportCsv.explanation_ja'), key: 'explanation_ja' },
    { header: t('MenuList.HeaderExportCsv.explanation_en'), key: 'explanation_en' },
    { header: t('MenuList.HeaderExportCsv.explanation_vi'), key: 'explanation_vi' },
    { header: t('MenuList.HeaderExportCsv.explanation_ko'), key: 'explanation_ko' },
    { header: t('MenuList.HeaderExportCsv.explanation_zh_hant'), key: 'explanation_zh_hant' },
    { header: t('MenuList.HeaderExportCsv.explanation_zh_hans'), key: 'explanation_zh_hans' },
    { header: t('MenuList.HeaderExportCsv.annotation_1'), key: 'annotation_1' },
    { header: t('MenuList.HeaderExportCsv.annotation1_ja'), key: 'annotation1_ja' },
    { header: t('MenuList.HeaderExportCsv.annotation1_en'), key: 'annotation1_en' },
    { header: t('MenuList.HeaderExportCsv.annotation1_vi'), key: 'annotation1_vi' },
    { header: t('MenuList.HeaderExportCsv.annotation1_ko'), key: 'annotation1_ko' },
    { header: t('MenuList.HeaderExportCsv.annotation1_zh_hant'), key: 'annotation1_zh_hant' },
    { header: t('MenuList.HeaderExportCsv.annotation1_zh_hans'), key: 'annotation1_zh_hans' },
    { header: t('MenuList.HeaderExportCsv.annotation_2'), key: 'annotation_2' },
    { header: t('MenuList.HeaderExportCsv.annotation2_ja'), key: 'annotation2_ja' },
    { header: t('MenuList.HeaderExportCsv.annotation2_en'), key: 'annotation2_en' },
    { header: t('MenuList.HeaderExportCsv.annotation2_vi'), key: 'annotation2_vi' },
    { header: t('MenuList.HeaderExportCsv.annotation2_ko'), key: 'annotation2_ko' },
    { header: t('MenuList.HeaderExportCsv.annotation2_zh_hant'), key: 'annotation2_zh_hant' },
    { header: t('MenuList.HeaderExportCsv.annotation2_zh_hans'), key: 'annotation2_zh_hans' },
    { header: t('MenuList.HeaderExportCsv.remarks'), key: 'remarks' },
  ];
  const headersMerge = [...headers, ...properties_headers];

  // Define custom event handlers
  useImperativeHandle(ref, () => ({
    onClick: () => {
      buttonRef.current?.click();
    },
  }));

  return (
    <>
      <ExportLink
        exportLinkRef={buttonRef}
        style={{ display: 'none' }}
        target="_blank"
        filename={'menu.csv'}
        data={extraData}
        headers={headersMerge}
        type="button"
        className="btn btn--dark-green btn-wrap"
      ></ExportLink>
    </>
  );
});

export default ExportCSV;
